import { useCallback, useEffect, useState } from 'react';
import type { Edge, Node } from '@xyflow/react';
import dagre from 'dagre';
import { supabase } from '../lib/supabase';

export type SupplyChainSupplier = {
  id: string;
  parent_id: string | null;
  company_name: string;
  registration_number: string;
  duns_number: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  city: string | null;
  address: string | null;
  country_risk: string | null;
  industry_risk: string | null;
  compliance_status: string | null;
  created_at: string | null;
  updated_at: string | null;
  organisation_id: string | null;
};

type SupplyChainSupplierRelationship = {
  supplier_id: string;
  parent_supplier_id: string;
  organisation_id: string | null;
  created_at: string | null;
};

export type SupplyChainNodeData = {
  supplier: SupplyChainSupplier;
  onSelectSupplier?: (id: string) => void;
  onAddSubSupplier?: (id: string) => void;
  onEditSupplier?: (id: string) => void;
};

export type SupplyChainNode = Node<SupplyChainNodeData>;
export type SupplyChainEdge = Edge;

export type CreateSupplyChainSupplierInput = {
  parent_id: string | null;
  parent_ids: string[];
  company_name: string;
  registration_number: string;
  duns_number: string | null;
  email: string | null;
  phone: string | null;
  country: string | null;
  city: string | null;
  address: string | null;
};

const DAGRE_NODE_WIDTH = 260;
const DAGRE_NODE_HEIGHT = 120;
const DAGRE_RANK_SEPARATION = 160;
const DAGRE_NODE_SEPARATION = 80;

const getEdgeStyleForSupplierStatus = (complianceStatus: string | null) => {
  if (complianceStatus === 'Verified') {
    return {
      style: {
        stroke: '#22c55e',
        strokeWidth: 2.5,
      },
    };
  }

  if (complianceStatus === 'Flagged' || complianceStatus === 'Expired') {
    return {
      style: {
        strokeDasharray: '6 4',
      },
    };
  }

  return {};
};

const applyRelationshipParents = (
  suppliers: Omit<SupplyChainSupplier, 'parent_id'>[],
  relationships: SupplyChainSupplierRelationship[],
): SupplyChainSupplier[] => {
  const supplierIds = new Set(suppliers.map((supplier) => supplier.id));
  const parentIdBySupplierId = new Map<string, string>();

  relationships.forEach((relationship) => {
    if (
      !supplierIds.has(relationship.supplier_id) ||
      !supplierIds.has(relationship.parent_supplier_id) ||
      parentIdBySupplierId.has(relationship.supplier_id)
    ) {
      return;
    }

    parentIdBySupplierId.set(
      relationship.supplier_id,
      relationship.parent_supplier_id,
    );
  });

  return suppliers.map((supplier) => ({
    ...supplier,
    parent_id: parentIdBySupplierId.get(supplier.id) ?? null,
  }));
};

const buildSupplyChainGraph = (
  suppliers: SupplyChainSupplier[],
  relationships: SupplyChainSupplierRelationship[],
) => {
  const supplierIds = new Set(suppliers.map((supplier) => supplier.id));
  const dagreGraph = new dagre.graphlib.Graph();

  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: 'TB',
    ranksep: DAGRE_RANK_SEPARATION,
    nodesep: DAGRE_NODE_SEPARATION,
    marginx: 32,
    marginy: 32,
  });

  suppliers.forEach((supplier) => {
    dagreGraph.setNode(supplier.id, {
      width: DAGRE_NODE_WIDTH,
      height: DAGRE_NODE_HEIGHT,
      label: supplier.company_name,
    });
  });

  const layoutRelationshipIds = new Set<string>();

  relationships
    .filter(
      (relationship) =>
        supplierIds.has(relationship.supplier_id) &&
        supplierIds.has(relationship.parent_supplier_id),
    )
    .forEach((relationship) => {
      const relationshipId = `${relationship.parent_supplier_id}-${relationship.supplier_id}`;

      if (layoutRelationshipIds.has(relationshipId)) {
        return;
      }

      layoutRelationshipIds.add(relationshipId);
      dagreGraph.setEdge(
        relationship.parent_supplier_id,
        relationship.supplier_id,
      );
    });

  dagre.layout(dagreGraph);

  const nodes: SupplyChainNode[] = suppliers.map((supplier) => ({
    id: supplier.id,
    type: 'supplier',
    position: (() => {
      const dagreNode = dagreGraph.node(supplier.id);

      if (!dagreNode) {
        return { x: 0, y: 0 };
      }

      return {
        x: dagreNode.x - DAGRE_NODE_WIDTH / 2,
        y: dagreNode.y - DAGRE_NODE_HEIGHT / 2,
      };
    })(),
    data: {
      supplier,
    },
  }));

  const renderedRelationshipIds = new Set<string>();
  const edges: SupplyChainEdge[] = relationships
    .filter(
      (relationship) =>
        supplierIds.has(relationship.supplier_id) &&
        supplierIds.has(relationship.parent_supplier_id),
    )
    .filter((relationship) => {
      const relationshipId = `${relationship.parent_supplier_id}-${relationship.supplier_id}`;

      if (renderedRelationshipIds.has(relationshipId)) {
        return false;
      }

      renderedRelationshipIds.add(relationshipId);
      return true;
    })
    .map((relationship) => {
      const supplier = suppliers.find(
        (item) => item.id === relationship.supplier_id,
      );

      return {
        id: `${relationship.parent_supplier_id}-${relationship.supplier_id}`,
        source: relationship.parent_supplier_id,
        target: relationship.supplier_id,
        type: 'smoothstep',
        ...getEdgeStyleForSupplierStatus(supplier?.compliance_status ?? null),
      };
    });

  return { nodes, edges };
};

export const useSupplyChain = (organisationId?: string) => {
  const [suppliers, setSuppliers] = useState<SupplyChainSupplier[]>([]);
  const [nodes, setNodes] = useState<SupplyChainNode[]>([]);
  const [edges, setEdges] = useState<SupplyChainEdge[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const syncSupplierRelationship = useCallback(
    async (
      supplierId: string,
      parentSupplierIds: string[] | null,
      mode: 'replace' | 'append' = 'replace',
    ) => {
      if (!organisationId) {
        throw new Error('Organisation is required');
      }

      const { data: existingRelationships, error: existingRelationshipsError } =
        await supabase
          .from('supply_chain_supplier_relationships')
          .select('parent_supplier_id')
          .eq('supplier_id', supplierId)
          .eq('organisation_id', organisationId);

      if (existingRelationshipsError) {
        throw existingRelationshipsError;
      }

      const nextParentSupplierIds = Array.from(
        new Set(
          (parentSupplierIds ?? []).filter(
            (parentSupplierId) =>
              Boolean(parentSupplierId) && parentSupplierId !== supplierId,
          ),
        ),
      );
      const currentParentSupplierIds = (
        (existingRelationships ?? []) as Array<{
          parent_supplier_id: string;
        }>
      ).map((relationship) => relationship.parent_supplier_id);
      const targetParentSupplierIds =
        mode === 'append'
          ? Array.from(
              new Set([...currentParentSupplierIds, ...nextParentSupplierIds]),
            )
          : nextParentSupplierIds;
      const parentSupplierIdsToDelete = currentParentSupplierIds.filter(
        (parentSupplierId) =>
          !targetParentSupplierIds.includes(parentSupplierId),
      );
      const parentSupplierIdsToInsert = targetParentSupplierIds.filter(
        (parentSupplierId) =>
          !currentParentSupplierIds.includes(parentSupplierId),
      );

      if (parentSupplierIdsToDelete.length > 0) {
        const { error: deleteRelationshipError } = await supabase
          .from('supply_chain_supplier_relationships')
          .delete()
          .eq('supplier_id', supplierId)
          .eq('organisation_id', organisationId)
          .in('parent_supplier_id', parentSupplierIdsToDelete);

        if (deleteRelationshipError) {
          throw deleteRelationshipError;
        }
      }

      if (parentSupplierIdsToInsert.length === 0) {
        return;
      }

      const { error: insertRelationshipError } = await supabase
        .from('supply_chain_supplier_relationships')
        .insert(
          parentSupplierIdsToInsert.map((parentSupplierId) => ({
            supplier_id: supplierId,
            parent_supplier_id: parentSupplierId,
            organisation_id: organisationId,
          })),
        );

      if (insertRelationshipError) {
        throw insertRelationshipError;
      }
    },
    [organisationId],
  );

  const fetchSupplyChain = useCallback(async () => {
    if (!organisationId) {
      setSuppliers([]);
      setNodes([]);
      setEdges([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const [
      { data: supplierData, error: supplierQueryError },
      { data: relationshipData, error: relationshipQueryError },
    ] = await Promise.all([
      supabase
        .from('supply_chain_suppliers')
        .select(
          'id, company_name, registration_number, duns_number, email, phone, country, city, address, country_risk, industry_risk, compliance_status, created_at, updated_at, organisation_id',
        )
        .eq('organisation_id', organisationId)
        .order('company_name', { ascending: true }),
      supabase
        .from('supply_chain_supplier_relationships')
        .select('supplier_id, parent_supplier_id, organisation_id, created_at')
        .eq('organisation_id', organisationId)
        .order('created_at', { ascending: true }),
    ]);

    if (supplierQueryError || relationshipQueryError) {
      console.error(
        'Error fetching supply chain data:',
        supplierQueryError ?? relationshipQueryError,
      );
      setSuppliers([]);
      setNodes([]);
      setEdges([]);
      setError(
        (supplierQueryError ?? relationshipQueryError)?.message ??
          'Unknown error',
      );
      setLoading(false);
      return;
    }

    const supplierRows = applyRelationshipParents(
      (supplierData ?? []) as Omit<SupplyChainSupplier, 'parent_id'>[],
      (relationshipData ?? []) as SupplyChainSupplierRelationship[],
    );
    const graph = buildSupplyChainGraph(
      supplierRows,
      (relationshipData ?? []) as SupplyChainSupplierRelationship[],
    );

    setSuppliers(supplierRows);
    setNodes(graph.nodes);
    setEdges(graph.edges);
    setLoading(false);
  }, [organisationId]);

  useEffect(() => {
    fetchSupplyChain();
  }, [fetchSupplyChain]);

  const createSupplier = useCallback(
    async (input: CreateSupplyChainSupplierInput) => {
      if (!organisationId) {
        throw new Error('Organisation is required');
      }

      setSaving(true);

      try {
        const { data: insertedSupplier, error: insertError } = await supabase
          .from('supply_chain_suppliers')
          .insert({
            company_name: input.company_name,
            registration_number: input.registration_number,
            duns_number: input.duns_number,
            email: input.email,
            phone: input.phone,
            country: input.country,
            city: input.city,
            address: input.address,
            organisation_id: organisationId,
          })
          .select('id')
          .single();

        if (insertError) {
          throw insertError;
        }

        await syncSupplierRelationship(insertedSupplier.id, input.parent_ids);
        await fetchSupplyChain();
      } finally {
        setSaving(false);
      }
    },
    [fetchSupplyChain, organisationId, syncSupplierRelationship],
  );

  const updateSupplier = useCallback(
    async (
      supplierId: string,
      input: CreateSupplyChainSupplierInput,
      options?: { parentRelationshipMode?: 'replace' | 'append' },
    ) => {
      if (!organisationId) {
        throw new Error('Organisation is required');
      }

      setSaving(true);

      try {
        const { error: updateError } = await supabase
          .from('supply_chain_suppliers')
          .update({
            company_name: input.company_name,
            registration_number: input.registration_number,
            duns_number: input.duns_number,
            email: input.email,
            phone: input.phone,
            country: input.country,
            city: input.city,
            address: input.address,
            updated_at: new Date().toISOString(),
          })
          .eq('id', supplierId)
          .eq('organisation_id', organisationId);

        if (updateError) {
          throw updateError;
        }

        await syncSupplierRelationship(
          supplierId,
          input.parent_ids,
          options?.parentRelationshipMode ?? 'replace',
        );
        await fetchSupplyChain();
      } finally {
        setSaving(false);
      }
    },
    [fetchSupplyChain, organisationId, syncSupplierRelationship],
  );

  return {
    suppliers,
    nodes,
    edges,
    loading,
    saving,
    error,
    createSupplier,
    updateSupplier,
    refetch: fetchSupplyChain,
  };
};
